/**
 * Event tracking logic for EventFoundry
 * Attaches click listeners and sends events to GA4
 */

import { isEditorActive, isPreviewActive } from './editor-overlay.js';
import { devLog } from './dev-logger.js';
import { getParentOrigin } from './parent-origin.js';
import { getAdapter } from './adapters/registry.js';
import { generateEventParams } from './params-generator.js';

// AbortController for managing event listener lifecycle
let abortController = null;

/**
 * Validate event definition
 * @param {Object} eventDef - Event definition from backend
 * @returns {boolean} - True if valid
 */
function validateEventDefinition(eventDef) {
  const { eventKey, eventLabel, selector, eventType, destinations } = eventDef;

  // Required fields
  if (!eventKey || typeof eventKey !== 'string') {
    console.error('EventFoundry: Invalid event - missing eventKey');
    return false;
  }

  if (!eventLabel || typeof eventLabel !== 'string') {
    console.error(`EventFoundry: Invalid event "${eventKey}" - missing eventLabel`);
    return false;
  }

  if (!selector || typeof selector !== 'string') {
    console.error(`EventFoundry: Invalid event "${eventKey}" - missing selector`);
    return false;
  }

  if (!eventType || eventType !== 'click') {
    console.error(`EventFoundry: Invalid event "${eventKey}" - unsupported eventType "${eventType}"`);
    return false;
  }

  if (!destinations || typeof destinations !== 'object') {
    console.warn(`EventFoundry: Event "${eventKey}" has no destinations`);
    // Don't reject - allow events with no destinations (will just not fire)
  }

  // Validate CSS selector
  try {
    document.querySelector(selector);
  } catch (error) {
    console.error(`EventFoundry: Invalid selector for event "${eventKey}": ${selector}`);
    return false;
  }

  return true;
}

/**
 * Validate event definitions and return validation results
 * @param {Array} definitions - Event definitions from API
 * @returns {Object} Object with validDefs and invalidDefs arrays
 */
export function validateSelectors(definitions) {
  const validDefs = [];
  const invalidDefs = [];

  definitions.forEach(def => {
    if (validateEventDefinition(def)) {
      validDefs.push(def);
    } else {
      invalidDefs.push({
        selector: def.selector,
        eventKey: def.eventKey,
        eventLabel: def.eventLabel
      });
    }
  });

  return { validDefs, invalidDefs };
}

/**
 * Fire event to all enabled destinations
 * @param {Object} eventDef - Event definition
 * @param {Object} params - Event parameters
 * @param {boolean} devMode - If true, log events but don't fire
 * @param {Object} initResults - Object mapping destination names to initialization status
 * @returns {string[]} - Array of destination names that received the event
 */
function fireEventToDestinations(eventDef, params, devMode, initResults) {
  const { eventKey, destinations } = eventDef;

  // Track which destinations actually received the event
  const firedDestinations = [];

  // Check if destinations object exists
  if (!destinations || Object.keys(destinations).length === 0) {
    devLog(devMode, 'EventFoundry: No destinations configured for this event');
    return firedDestinations;
  }

  // Iterate over event's destination config
  for (const [destName, destConfig] of Object.entries(destinations)) {
    // Skip if destination not enabled for this event
    if (!destConfig.enabled) {
      continue;
    }

    // Skip if destination failed to initialize
    if (!initResults[destName]) {
      console.warn(`EventFoundry: ${destName} not initialized, skipping event`);
      continue;
    }

    const adapter = getAdapter(destName);
    if (!adapter) {
      continue;
    }

    if (devMode) {
      // Dev mode: Log but don't fire
      console.log(`[DEV] Would fire to ${destName}:`, eventKey, params);
      firedDestinations.push(destName);
    } else {
      // Production: Fire to destination
      try {
        adapter.fireEvent(eventKey, params);
        firedDestinations.push(destName);
      } catch (error) {
        console.error(`EventFoundry: Error firing to ${destName}:`, error);
      }
    }
  }

  return firedDestinations;
}

/**
 * Initialize event tracking
 * @param {Array} definitions - Event definitions from API
 * @param {boolean} devMode - If true, log events but don't send to destinations
 * @param {Object} initResults - Object mapping destination names to initialization status
 * @returns {boolean} True if tracking was initialized, false if all selectors were invalid
 */
export function initTracking(definitions, devMode = false, initResults = {}) {
  if (!definitions || definitions.length === 0) {
    console.log('EventFoundry: No events defined for this site');
    return true; // Not an error - site simply has no events configured
  }

  // Prevent duplicate listeners on re-initialization
  if (abortController) {
    abortController.abort();
  }

  // Validate all selectors upfront to catch invalid ones before attaching listeners
  const { validDefs, invalidDefs } = validateSelectors(definitions);

  if (validDefs.length === 0) {
    // All events are invalid - possible cache corruption
    console.error('🚨 EventFoundry: All event selectors invalid - possible cache corruption');
    console.error('Invalid events:', invalidDefs);
    return false; // Signal failure to trigger cache clear
  }

  // Warn if SOME selectors are invalid
  if (invalidDefs.length > 0) {
    console.warn(`EventFoundry: ${invalidDefs.length} of ${definitions.length} selectors are invalid and will be skipped`);
  }

  // Create new AbortController for this initialization
  abortController = new AbortController();

  document.addEventListener('click', (event) => {
    // Prevent firing GA4 events during element selection in edit mode
    if (isEditorActive() && !isPreviewActive()) {
      devLog(devMode, 'EventFoundry: Click ignored (editor mode active, not in preview)');
      return;
    }

    // Find ALL matching definitions for this click (only valid ones)
    const matchingDefs = validDefs.filter(def => {
      return event.target.closest(def.selector) !== null;
    });

    if (matchingDefs.length === 0) {
      devLog(devMode, 'EventFoundry: Click on', event.target, 'matched 0 events');
      return;
    }

    // MVP: Fire only the FIRST matching event
    const defToFire = matchingDefs[0];

    // Get the clicked element that matched the selector
    const clickedElement = event.target.closest(defToFire.selector);

    // Generate auto-parameters using centralized generator
    const params = generateEventParams(clickedElement, defToFire);

    // If in preview mode, send EVENT_FIRED postMessage to parent for event log
    if (isPreviewActive()) {
      // Get list of enabled destinations for this event (without firing)
      const enabledDestinations = defToFire.destinations
        ? Object.entries(defToFire.destinations)
            .filter(([_, config]) => config.enabled && initResults[_])
            .map(([name, _]) => name)
        : [];

      try {
        const targetOrigin = getParentOrigin();
        if (targetOrigin) {
          window.parent.postMessage({
            type: 'EVENT_FIRED',
            timestamp: Date.now(),
            eventLabel: defToFire.eventLabel,
            eventKey: defToFire.eventKey,
            selector: defToFire.selector,
            destinations: enabledDestinations,
            params: params,
            pagePath: window.location.pathname
          }, targetOrigin);
          console.log('→ EVENT_FIRED sent to parent');
        } else {
          console.warn('EventFoundry: Cannot send EVENT_FIRED - parent origin not established');
        }
      } catch (error) {
        console.warn('EventFoundry: Failed to send EVENT_FIRED postMessage', error);
      }
    } else {
      // Fire to all enabled destinations (production or dev mode)
      const firedDestinations = fireEventToDestinations(defToFire, params, devMode, initResults);

      // Log event about to fire in dev mode
      devLog(devMode, `EventFoundry: Event fired to ${firedDestinations.length} destination(s):`, firedDestinations);
    }

    // Warn about multiple matches in development
    if (matchingDefs.length > 1) {
      console.warn('EventFoundry: Multiple events matched, only firing first:',
        matchingDefs.map(d => d.eventLabel)
      );
    }
  }, { capture: true, signal: abortController.signal });

  return true;
}
